import React, { useMemo } from 'react'
import { graphql, useFragment } from 'react-relay/hooks'
import { keyBy, map } from 'lodash'
import { useDecision } from '@optimizely/react-sdk'
import { useApi, useMountEffect } from 'hooks'
import ChoiceButton from 'components/ui/input/ChoiceButton'
import IntegrationStatus from 'components/integrations/IntegrationStatus'
import { FlexRow, Text, FlexColumn } from 'elements'

const integrationLabels = {
  intuit: 'QuickBooks® Online',
  xero: 'Xero®',
}

export default function IntegrationChoices({ company, selected, onSelect }) {
  const api = useApi()

  //optimizely feature
  const [decision] = useDecision('canCreateXeroReports')
  const xeroEnabled = decision.enabled

  const data = useFragment(
    graphql`
      fragment IntegrationChoices_company on Company {
        id
        rowId
        integrations(first: 100) @connection(key: "IntegrationChoices_integrations") {
          edges {
            node {
              id
              rowId
              kind
              config
              status
            }
          }
        }
        organization {
          slug
        }
      }
    `,
    company,
  )

  const integrationOptions = useMemo(() => {
    const options = {
      intuit: {
        kind: 'intuit',
        href: api.integration.getConnectUrl(data.rowId, 'intuit', {
          modal: 'create-report',
          modalBehavior: 'semi',
          orgSlug: data?.organization?.slug,
          companyId: data?.id,
        }),
      },
      xero: {
        kind: 'xero',
        href: api.integration.getConnectUrl(data.rowId, 'xero', {
          modal: 'create-report',
          modalBehavior: 'semi',
          orgSlug: data?.organization?.slug,
          companyId: data?.id,
        }),
      },
    }

    if (!xeroEnabled) {
      delete options.xero
    }

    return options
  }, [api, data, xeroEnabled])

  const integrations = data?.integrations?.edges?.map((edge) => edge.node)

  useMountEffect(() => {
    const match = integrations?.find(
      (integration) => integration.rowId === selected?.rowId || integration.kind === selected?.kind,
    )

    const integrationOption = integrationOptions[selected?.kind]
    const isEnabled = match?.status === 'ENABLED'

    if (match) {
      if (isEnabled) {
        onSelect(match)
      } else {
        onSelect({ ...match, href: integrationOption.href })
      }
    } else {
      onSelect(integrationOption)
    }
  })

  const integrationsByKind = keyBy(integrations, 'kind')

  return (
    <FlexColumn width="100%">
      {map(integrationOptions, (option, kind) => {
        const integration = integrationsByKind[kind]

        const isLast = xeroEnabled ? kind === 'xero' : true
        const isSelected = selected?.kind === kind

        // Integration exists, so allow the user to select
        // it and proceed with the report
        if (integration) {
          const isEnabled = integration.status === 'ENABLED'

          return (
            <ChoiceButton
              mb={!isLast ? 'large' : null}
              key={kind}
              icon="database"
              selected={isSelected}
              onPress={() => {
                if (isEnabled) {
                  onSelect(integration)
                } else {
                  //add href for disabled integrations, so that continuing
                  //redirects you to connection screen
                  onSelect({ ...integration, href: option.href })
                }
              }}
            >
              <FlexRow width="100%" justifyContent="space-between">
                <Text color="inherit">{integrationLabels[integration.kind]} Integration</Text>
                <IntegrationStatus
                  userIntegration={integration}
                  status={integration?.status?.toLowerCase()}
                  color="inherit"
                  showConnected={
                    integration?.status?.toLowerCase() === 'enabled' ? 'Connected' : null
                  }
                />
              </FlexRow>
            </ChoiceButton>
          )
        }

        return (
          <ChoiceButton
            mb={!isLast ? 'large' : null}
            key={kind}
            icon="database"
            selected={selected?.kind === kind}
            onPress={() => onSelect(option)}
          >
            {integrationLabels[kind]} Integration
          </ChoiceButton>
        )
      })}
    </FlexColumn>
  )
}
